// ---------------------------------------------------------------------------
// THEME — the one file to edit to restyle the whole game.
//
// These values are written onto :root as CSS custom properties before React
// renders, and Tailwind's tokens resolve through them. Change a hex here and
// the TV, the phones and the admin page all follow.
// ---------------------------------------------------------------------------

export const theme = {
  brand: {
    name: "Swyftomate Games",
    short: "Swyftomate",
    /** Shown on the host screen while the lobby is open. */
    tagline: "Trivia, made up on the spot",
  },

  colors: {
    /** The stage backdrop. Aubergine-black, not neutral black — warmer on a big panel. */
    stage: "#17092B",
    /** Raised surfaces: panels, the code tiles, the settings tray. */
    stage2: "#24103F",
    /** Hairlines and dividers. Low contrast on purpose. */
    haze: "#3B1D63",
    /** The spotlight. Brand accent, focus rings, glows. */
    spot: "#B57BFF",
    /** Victory only — podium, winner, awards. Never decoration. */
    gold: "#FFC531",
    /** Text. A lilac-warm white rather than pure #FFF, which glares on a TV. */
    chalk: "#F7F2FF",
    /** Secondary text. */
    mute: "#A692C4",
  },

  /**
   * The four answer colours. Index matters: it lines up with OPTION_SHAPES in
   * the shared package, so option 0 is always the pink triangle on every
   * screen in the room. Red is pushed to hot pink so it stays separable from
   * the green for red-green colour blindness — the shapes are the real
   * guarantee, this is the belt to that braces.
   */
  answers: [
    { name: "flare", hex: "#FF3D71", ink: "#2B0010" },
    { name: "volt", hex: "#00C2FF", ink: "#001C26" },
    { name: "sun", hex: "#FFC531", ink: "#2B1D00" },
    { name: "fern", hex: "#2BD980", ink: "#002615" },
  ],

  radius: {
    /** Code tiles — slab, like a scoreboard. */
    tile: "10px",
    /** Panels and trays. */
    panel: "24px",
    /** Player pills and chips — fully round. */
    pill: "999px",
    /** Answer buttons on the phone. */
    answer: "20px",
  },
} as const;

export type Theme = typeof theme;

/** Written before first paint so there's no unstyled flash. */
export function applyTheme(root: HTMLElement = document.documentElement): void {
  const { colors, radius } = theme;
  const vars: Record<string, string> = {
    "--brand-stage": colors.stage,
    "--brand-stage-2": colors.stage2,
    "--brand-haze": colors.haze,
    "--brand-spot": colors.spot,
    "--brand-gold": colors.gold,
    "--brand-chalk": colors.chalk,
    "--brand-mute": colors.mute,
    "--radius-tile": radius.tile,
    "--radius-panel": radius.panel,
    "--radius-pill": radius.pill,
    "--radius-answer": radius.answer,
  };

  theme.answers.forEach((answer, i) => {
    vars[`--answer-${i}`] = answer.hex;
    vars[`--answer-ink-${i}`] = answer.ink;
  });

  for (const [key, value] of Object.entries(vars)) root.style.setProperty(key, value);
}
